import {
  AfterContentInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import { Store } from '@ngrx/store';
import { CheckableSettings, FilterExpandSettings } from '@progress/kendo-angular-treeview';
import { State } from '@progress/kendo-data-query/dist/npm/state';
import { TranslationLoaderFacade } from 'src/app/admin/localization/translation-loader.facade';
import { LocationService } from 'src/app/admin/location/shared/services/location.service';
import { LocationSelectorInfo } from 'src/app/admin/user/shared/models/locationSelectorModel';
import { LocationMultiSelectorModel } from 'src/app/shared/models/location-multi-selector.model';
import { LocationDropDownModel } from 'src/app/shared/models/locations-drop-down-list.model';
import * as CoreActions from 'src/app/state/actions/core.actions';

@Component({
  selector: 'location-multi-selector-tree-view',
  templateUrl: './location-multi-selector-tree-view.component.html',
  styleUrls: ['./location-multi-selector-tree-view.component.scss']
})
export class LocationMultiSelectorTreeViewComponent implements OnInit, AfterContentInit {
  // get root locations ids as input
  @Input() expandedKeys: any[] = [];
  // get checked components Tree view unique id0
  @Input() selectedLocationId: number = 0;

  @Input() locations: LocationMultiSelectorModel[] = [];
  @Input() locationsFlatData: LocationMultiSelectorModel[] = [];
  @Input() dispatchStateEvent: boolean = true;
  @Output()
  closeEvent: EventEmitter<LocationSelectorInfo> = new EventEmitter<LocationSelectorInfo>();

  @Output() onChange: EventEmitter<any> = new EventEmitter();

  public locationSelector: LocationSelectorInfo = {
    id: 0,
    locationName: '',
    locationCode: '',
    utcoffsetInMinutes: 0,
    locationTypeId: 0,
    timezoneId: '',
    timeZoneName: '',
    dropDownDisabled: false,
    lastLocationAutoWraupTime: ''
  };

  // currently checked Tree view unique id
  @Input()
  public checkedKeys: any[] = [];
  ngOnChanges(changes: SimpleChanges) {
    this.checkDefaultValue(changes.checkedKeys.currentValue, this.locations);
    // You can also use categoryId.previousValue and 
    // categoryId.firstChange for comparing old and new values
  }

  public checkAll: boolean = true;

  // Checking component
  public key = 'locationId';
  public iterableDiffer: any;

  constructor(private locationService: LocationService,
    private store: Store<State>,
    private cdRef: ChangeDetectorRef,
    private translationLoaderFacade: TranslationLoaderFacade) { }

  public ngOnInit(): void {
    this.checkDefaultValue(this.checkedKeys, this.locations);
    this.onChange.emit(this.checkedKeys);
  }

  ngAfterContentInit() {
    this.addExpandListItem(this.locations);
  }

  ngAfterViewInit() {
    this.cdRef.detectChanges();
  }

  public checkDefaultValue(checkedKeys: number[], items?: LocationMultiSelectorModel[]) {
    if (!items) return;

    for (const item of items) {
      if (checkedKeys.includes(item.locationId)) {
        if (item.isSelectable) {
          item.isSelected = true;
          this.checkDefaultValue(checkedKeys, item.items);
        } else {
          item.isSelected = false;
        }
      }
      else {
        item.isSelected = false;
        this.checkDefaultValue(checkedKeys, item.items)
      }
    }
  }

  public handleCheck(dataItem: LocationMultiSelectorModel) {
    dataItem.isSelected = !dataItem.isSelected;

    this.handleChildCheck(dataItem.items, dataItem.isSelected);

    this.checkedKeys = [];
    this.checkedKeys = this.getCheckedKeys(this.locations, this.checkedKeys);
    this.onChange.emit(this.checkedKeys);

  }

  public handleChildCheck(childItems?: LocationMultiSelectorModel[], isSelected: boolean = false) {
    if (!childItems) return;

    for (const childItem of childItems) {
      if (childItem.isSelectable) {
        childItem.isSelected = isSelected;
        this.handleChildCheck(childItem.items, isSelected);
      }
    }
  }

  public unCheckAll(items?: LocationMultiSelectorModel[]) {
    if (!items) return;

    for (const item of items) {
      if (item.isSelectable) {
        item.isSelected = false;
        this.unCheckAll(item.items);
      }
    }
  }

  public getCheckedKeys(items?: LocationMultiSelectorModel[], keys: number[] = []): number[] {
    if (!items) return [];

    for (const item of items) {
      if (item.isSelected) {
        keys.push(item.locationId);
      }
      keys = this.getCheckedKeys(item.items, keys);

    }
    return keys;
  }

  /**
 * Handles the click event on a location item in the selector.
 * Populates `locationSelector` with the clicked item's data.
 * Dispatches an update action if `dispatchStateEvent` is true.
 * Emits a 'closeEvent' with the updated `locationSelector`.
 */
  showClick(dataItem: any) {
    this.locationSelector = {
      id: dataItem?.locationId,
      locationName: dataItem?.locationName,
      locationCode: dataItem?.locationCode,
      locationTypeId: dataItem?.locationTypeId,
      utcoffsetInMinutes: dataItem?.utcoffsetInMinutes,
      timezoneId: dataItem?.timezoneId,
      timeZoneName: dataItem?.timeZoneName,
      dropDownDisabled: false,
      lastLocationAutoWraupTime: dataItem?.lastLocationAutoWraupTime

    };

    if (this.dispatchStateEvent == true) {
      this.store.dispatch(CoreActions.updateLocationSelectorInfo({
        payload: {
          locationSelectorInfo: this.locationSelector
        }
      }));
    }

    this.closeEvent.emit(this.locationSelector);
  }

  // check item has children
  public hasChildren(item: LocationDropDownModel) {
    return item.hasChild;
  }

  // fetch childrend data
  public fetchChildren = (item: any) => this.locationService.getHierarchyChildLocations(item.locationId);

  // checking settings
  public get checkableSettings(): CheckableSettings {
    return {
      checkChildren: true,
      checkParents: true,
      enabled: true,
      mode: 'multiple',
      checkOnClick: true,
    };
  }

  public filterExpandSettings: FilterExpandSettings = {
    maxAutoExpandResults: 4,
    expandedOnClear: "unchanged"
  };

  private addExpandListItem(treeItem?: LocationDropDownModel[]) {
    if (!treeItem) return;

    treeItem.forEach(item => {
      if (item.hasChild) {
        this.addExpandListItem(item.items)
      }
    });
  }
}
